import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { NetworkQualityService, NetworkInfo, ConnectionQuality } from '../services/performance/NetworkQualityService';

/**
 * Priorité de chargement des composants
 */
export type LoadPriority = 'critical' | 'high' | 'medium' | 'low' | 'lazy';

/**
 * État de chargement d'un composant
 */
export type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Configuration de chargement pour un composant
 */
export interface LoadConfig {
  id: string;
  priority: LoadPriority;
  state: LoadState;
  progress?: number;
  error?: Error;
}

/**
 * Contexte de performance global
 */
interface PerformanceContextType {
  // Informations réseau
  networkInfo: NetworkInfo;
  connectionQuality: ConnectionQuality;
  isOnline: boolean;
  
  // Recommandations de chargement
  recommendations: ReturnType<typeof NetworkQualityService.getLoadingRecommendations>;
  
  // Gestion des composants
  registerComponent: (id: string, priority: LoadPriority) => void;
  unregisterComponent: (id: string) => void;
  setComponentState: (id: string, state: LoadState, progress?: number) => void;
  getComponentState: (id: string) => LoadConfig | undefined;
  
  // Vérification de chargement
  shouldLoad: (priority: LoadPriority) => boolean;
  canLoadMore: () => boolean;
  
  // État global
  isAppReady: boolean;
  criticalComponentsLoaded: boolean;
  loadingProgress: number;
  activeLoads: number;
}

const PerformanceContext = createContext<PerformanceContextType | null>(null);

/**
 * Délais de chargement par priorité (en ms)
 */
const PRIORITY_DELAYS: Record<LoadPriority, Record<ConnectionQuality, number>> = {
  critical: { excellent: 0, good: 0, moderate: 0, slow: 0, offline: 0 },
  high: { excellent: 0, good: 50, moderate: 100, slow: 200, offline: 500 },
  medium: { excellent: 100, good: 200, moderate: 400, slow: 800, offline: 1500 },
  low: { excellent: 300, good: 500, moderate: 1000, slow: 2000, offline: 3000 },
  lazy: { excellent: 500, good: 1000, moderate: 2000, slow: 5000, offline: 10000 },
};

/**
 * Provider de performance
 */
export function PerformanceProvider({ children }: { children: React.ReactNode }) {
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>(NetworkQualityService.getNetworkInfo());
  const [components, setComponents] = useState<Map<string, LoadConfig>>(new Map());
  const [activeLoads, setActiveLoads] = useState(0);

  // S'abonner aux changements réseau
  useEffect(() => {
    const unsubscribe = NetworkQualityService.subscribe(setNetworkInfo);
    return unsubscribe;
  }, []);

  // Recommandations basées sur la qualité réseau
  const recommendations = useMemo(() => {
    return NetworkQualityService.getLoadingRecommendations();
  }, [networkInfo.quality]);

  // Enregistrer un composant
  const registerComponent = useCallback((id: string, priority: LoadPriority) => {
    setComponents(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(id)) {
        newMap.set(id, { id, priority, state: 'idle' });
      }
      return newMap;
    });
  }, []);

  // Désenregistrer un composant
  const unregisterComponent = useCallback((id: string) => {
    setComponents(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  // Mettre à jour l'état d'un composant
  const setComponentState = useCallback((id: string, state: LoadState, progress?: number) => {
    setComponents(prev => {
      const newMap = new Map(prev);
      const config = newMap.get(id);
      if (config) {
        newMap.set(id, { ...config, state, progress });
        
        // Mettre à jour le compteur de chargements actifs
        if (state === 'loading' && config.state !== 'loading') {
          setActiveLoads(a => a + 1);
        } else if (state !== 'loading' && config.state === 'loading') {
          setActiveLoads(a => Math.max(0, a - 1));
        }
      }
      return newMap;
    });
  }, []);

  // Obtenir l'état d'un composant
  const getComponentState = useCallback((id: string): LoadConfig | undefined => {
    return components.get(id);
  }, [components]);

  // Vérifier si un composant devrait charger
  const shouldLoad = useCallback((priority: LoadPriority): boolean => {
    if (!networkInfo.isOnline && priority !== 'critical') {
      return false;
    }

    // Les composants critiques chargent toujours
    if (priority === 'critical') return true;

    // En mode économie de données, ne charger que critical et high
    if (networkInfo.saveData && priority !== 'high') {
      return false;
    }

    // Vérifier si on peut charger plus
    if (activeLoads >= recommendations.parallelLoads && priority !== 'high') {
      return false;
    }

    return true;
  }, [networkInfo, activeLoads, recommendations.parallelLoads]);

  // Vérifier si on peut charger plus de composants
  const canLoadMore = useCallback((): boolean => {
    return activeLoads < recommendations.parallelLoads;
  }, [activeLoads, recommendations.parallelLoads]);

  // Calculer si les composants critiques sont chargés
  const criticalComponentsLoaded = useMemo(() => {
    const criticalComponents = Array.from(components.values()).filter(c => c.priority === 'critical');
    if (criticalComponents.length === 0) return true;
    return criticalComponents.every(c => c.state === 'loaded');
  }, [components]);

  // Calculer le progrès global de chargement
  const loadingProgress = useMemo(() => {
    const componentsList = Array.from(components.values());
    if (componentsList.length === 0) return 100;
    
    const loaded = componentsList.filter(c => c.state === 'loaded').length;
    return Math.round((loaded / componentsList.length) * 100);
  }, [components]);

  // L'application est prête quand les composants critiques sont chargés
  const isAppReady = criticalComponentsLoaded;

  const value: PerformanceContextType = {
    networkInfo,
    connectionQuality: networkInfo.quality,
    isOnline: networkInfo.isOnline,
    recommendations,
    registerComponent,
    unregisterComponent,
    setComponentState,
    getComponentState,
    shouldLoad,
    canLoadMore,
    isAppReady,
    criticalComponentsLoaded,
    loadingProgress,
    activeLoads,
  };

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
}

/**
 * Hook pour accéder au contexte de performance
 */
export function usePerformance(): PerformanceContextType {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
}

/**
 * Hook pour le chargement prioritaire d'un composant
 */
export function usePriorityLoad(id: string, priority: LoadPriority = 'medium') {
  const {
    registerComponent,
    unregisterComponent,
    setComponentState,
    shouldLoad,
    connectionQuality,
    recommendations
  } = usePerformance();

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Enregistrer le composant au montage
  useEffect(() => {
    registerComponent(id, priority);
    return () => unregisterComponent(id);
  }, [id, priority, registerComponent, unregisterComponent]);

  // Gérer le chargement basé sur la priorité
  useEffect(() => {
    if (isReady || isLoading) return;

    const delay = PRIORITY_DELAYS[priority][connectionQuality];
    
    const timer = setTimeout(() => {
      if (shouldLoad(priority)) {
        setIsLoading(true);
        setComponentState(id, 'loading');
        
        // Simuler un court délai de chargement pour les animations
        requestAnimationFrame(() => {
          setIsReady(true);
          setIsLoading(false);
          setComponentState(id, 'loaded');
        });
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [id, priority, shouldLoad, setComponentState, connectionQuality, isReady, isLoading]);

  return {
    isReady,
    isLoading,
    shouldRender: isReady,
    showSkeleton: !isReady,
    enableAnimations: recommendations.enableAnimations,
    reducedMode: recommendations.reducedMode,
  };
}

/**
 * Hook pour le chargement de données avec priorité
 */
export function usePriorityData<T>(
  id: string,
  fetcher: () => Promise<T>,
  priority: LoadPriority = 'medium',
  deps: React.DependencyList = []
) {
  const {
    registerComponent,
    unregisterComponent,
    setComponentState,
    shouldLoad,
    connectionQuality,
    recommendations
  } = usePerformance();

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    registerComponent(id, priority);
    return () => unregisterComponent(id);
  }, [id, priority, registerComponent, unregisterComponent]);

  useEffect(() => {
    if (!shouldLoad(priority)) return;

    const delay = PRIORITY_DELAYS[priority][connectionQuality];

    const timer = setTimeout(async () => {
      setIsLoading(true);
      setComponentState(id, 'loading');
      
      try {
        const result = await fetcher();
        setData(result);
        setError(null);
        setComponentState(id, 'loaded');
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setComponentState(id, 'error');
      } finally {
        setIsLoading(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [id, priority, shouldLoad, setComponentState, connectionQuality, ...deps]);

  return {
    data,
    isLoading,
    error,
    isReady: data !== null && !isLoading,
    reducedMode: recommendations.reducedMode,
  };
}

/**
 * Hook simplifié pour la qualité réseau
 */
export function useNetworkQuality() {
  const { networkInfo, connectionQuality, isOnline, recommendations } = usePerformance();
  
  return {
    quality: connectionQuality,
    isOnline,
    isSlow: connectionQuality === 'slow' || connectionQuality === 'offline',
    isFast: connectionQuality === 'excellent' || connectionQuality === 'good',
    saveData: networkInfo.saveData,
    ...recommendations
  };
}
